import "react-datepicker/dist/react-datepicker.css";
import { useCallback, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  SizeColumnsToContentStrategy,
  ValueFormatterParams,
  ValueFormatterFunc
} from "ag-grid-enterprise";

import useSidebar from "src/hooks/useSidebar";
import useReport from "src/hooks/useReport";

import BaseGrid from "../../BaseGrid";
import LoadingComponent from "src/components/LoadingComponent";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { downloadFile } from "src/utils/DownloadFile";
import { formatCurrency } from "src/utils/FormatCurrency";

import { CardholderLiabilityDataModel } from "src/models/CardholderLiabilityDataModel";

import useReference from "src/hooks/useReference";

const CardholderLiability = () => {
  const { setIsOpen } = useSidebar();
  const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

  const [gridReady, setGridReady] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { currencyList } = useReference();

  const numericToAlpha = useMemo<Record<string, string>>(() => {
      const map: Record<string, string> = {};
      (currencyList ?? []).forEach((c: any) => {
          if (c?.numericCode != null && c?.alphaCode) {
              map[String(c.numericCode)] = String(c.alphaCode).toUpperCase();
          }
      });
      return map;
  }, [currencyList]);

  const resolveAlphaFromRow = useCallback((data?: CardholderLiabilityDataModel | null): string => {
      const feeVal = data?.currencyNumericCode;
      if (feeVal == null) return "USD";
      const str = String(feeVal).trim();
      if (/^[A-Za-z]{3}$/.test(str)) return str.toUpperCase();
      const mapped = numericToAlpha[String(str)];
      if (mapped) return mapped;
      return "USD";
  }, [numericToAlpha]);


  const currencyFormatter = useCallback<ValueFormatterFunc<CardholderLiabilityDataModel, any>>(
      (params: ValueFormatterParams<CardholderLiabilityDataModel, any>): string => {
          const v = params?.value;
          if (v === null || v === undefined) return "";
          const n = typeof v === "number" ? v : Number(v);
          if (Number.isNaN(n)) return "";
          const alphaCode = resolveAlphaFromRow(params.data);
          return String(formatCurrency(n, alphaCode));
      },
      [resolveAlphaFromRow]
  );

  const colDefs = useMemo<ColDef<CardholderLiabilityDataModel>[]>(() => [
      { headerName: "ICA", field: "ica" },
      { field: "bin" },
      { field: "program" },
      { field: "date" },

      {
          headerName: "Previous Day Balance With Derecognition",
          field: "previousDayDerecTransactionBalance",
          valueFormatter: currencyFormatter,
      },
      {
          headerName: "Previous Day Balance",
          field: "previousDayTransactionBalance",
          valueFormatter: currencyFormatter,
      },

      { field: "transferAmount", valueFormatter: currencyFormatter },
      { field: "issuedCardCount" },
      { field: "issuedAmount", valueFormatter: currencyFormatter },

      { field: "reloadCardCount" },
      { field: "reloadCardAmount", valueFormatter: currencyFormatter },

      { field: "feePaymentAmount", valueFormatter: currencyFormatter },
      { headerName: "Other ACH Funds", field: "otherACHFundsAmount", valueFormatter: currencyFormatter },

      { headerName: "Received Settlement Amount", field: "receivedSettlementAmount", valueFormatter: currencyFormatter },
      { headerName: "Transferred Settlement Amount", field: "transferredSettlementAmount", valueFormatter: currencyFormatter },

      { field: "unloadCardCount" },
      { field: "unloadCardAmount", valueFormatter: currencyFormatter },

      { field: "atmTransactionCount" },
      { field: "atmTransactionAmount", valueFormatter: currencyFormatter },

      { headerName: "Cardholder Fees", field: "cardholderFeeAmount", valueFormatter: currencyFormatter },
      {
          headerName: "Other Non-Cardholder Settlement Amount",
          field: "unpostedSettlementAmount",
          valueFormatter: currencyFormatter,
      },

      { field: "manualAdjustmentAmount", valueFormatter: currencyFormatter },

      { field: "accountMaintenanceCount" },
      { field: "accountMaintenanceAmount", valueFormatter: currencyFormatter },
      { field: "taxAmount", valueFormatter: currencyFormatter },

      { field: "distributorRefundCardCount" },
      { field: "distributorRefundAmount", valueFormatter: currencyFormatter },

      { field: "expiredFundsAmount", valueFormatter: currencyFormatter },
      { field: "expiredFundsTaxAmount", valueFormatter: currencyFormatter },

      { field: "unloadToAchCount" },
      { field: "unloadToAchAmounts", valueFormatter: currencyFormatter },
      { field: "unloadToAchFeeAmounts", valueFormatter: currencyFormatter },

      { field: "directPayCount" },
      { field: "directPayAmount", valueFormatter: currencyFormatter },

      { field: "loadMobileWalletCount" },
      { field: "loadMobileWalletAmount", valueFormatter: currencyFormatter },

      { field: "unloadMobileWalletCount" },
      { field: "unloadMobileWalletAmount", valueFormatter: currencyFormatter },

      { field: "delegationUnloadCount" },
      { field: "delegationUnloadAmount", valueFormatter: currencyFormatter },

      { field: "negativeBalanceAdjustmentCount" },
      { field: "negativeBalanceAdjustmentAmount", valueFormatter: currencyFormatter },

      { field: "consumerLoadFundsCount" },
      { field: "consumerLoadFundsAmount", valueFormatter: currencyFormatter },

      { field: "partnerUnloadCount" },
      { field: "partnerUnloadAmount", valueFormatter: currencyFormatter },

      { headerName: "Daily Transaction Balance", field: "dailyTransactionBalance", valueFormatter: currencyFormatter },
      { headerName: "Recaptured Amount", field: "recaptureAmounts", valueFormatter: currencyFormatter },
      { headerName: "Derecognized Amount", field: "derecognitionAmounts", valueFormatter: currencyFormatter },

      {
          headerName: "Daily Balance With Derecognition",
          field: "dailyDerecTransactionBalance",
          valueFormatter: currencyFormatter,
      },

      { headerName: "UniqueTag", field: "programUniqueTag" },
  ], [currencyFormatter]);

  const defaultColDef: ColDef = {
    sortable: false,
    filter: false,
    cellStyle: { textAlign: "center" },
  };

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
      defaultMinWidth: 90,
      defaultMaxWidth: 150,
      skipHeader: false,
    };
  }, []);

  const handleViewReport = () => {
    setIsDownload(false);
    setGridReady(true);
    setDataSource(createServerSideDatasource());
  };

  const handleDownloadReport = () => {
    setIsDownload(true);
    setIsLoading(true);

    ReportApi.exportCardholderLiability(mapReportRequest(null))
      .then((res) => {
        downloadFile(CardholderLiability.displayName!, res.data);
      })
      .finally(() => setIsLoading(false));
  };

  const createServerSideDatasource = (): IServerSideDatasource => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);

    ReportApi.getCardholderLiability(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        const success: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if (result?.rows?.length > 0) {
          params?.api?.hideOverlay();
          params?.success(success);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }
      })
      .catch(() => params?.fail?.());
  };

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">Cardholder Liability Report</h5>
        </div>

        <div className="blocks">
          <SearchCriteriaRedReports
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
            useIcas={true}
            useCountries={true}
            useBins={true}
            usePrograms={true}
            useBankIds={true}
            showToDate={false}
          />
        </div>

        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};

CardholderLiability.displayName = "CardholderLiability";

export default CardholderLiability;
